
import React, { useEffect } from 'react';
import { useNavigate } from '../../routes';
import { useAppPathname } from '../../navigation/useAppPathname';
import { useAppStore } from '../../store';
import { UserRole, ROUTES } from '@repo/shared-types';
import { DashboardView } from './DashboardView';
import { TenantDashboardView } from '../tenant/TenantDashboardView';
import { VendorDashboardView } from '../vendor/VendorDashboardView';
import { OwnerDashboardView } from '../owner/OwnerDashboardView';
import { InvestorDashboardView } from '../investor/InvestorDashboardView';
import { AlertCircle } from 'lucide-react';

/**
 * Renders the appropriate dashboard based on the current user's role.
 * Acts as the "Index" route for /app.
 */
export const RoleBasedHome: React.FC = () => {
  const { userRole } = useAppStore();
  const navigate = useNavigate();
  const pathname = useAppPathname();

  useEffect(() => {
    if (userRole === UserRole.TENANT && pathname === ROUTES.APP) {
      navigate(ROUTES.MY_HOME, { replace: true });
    }
  }, [userRole, pathname, navigate]);

  const renderContent = () => {
    switch (userRole) {
      case UserRole.PROPERTY_MANAGER:
      case UserRole.ADMIN:
      case UserRole.SYSTEM_ADMIN:
      case UserRole.PLATFORM_ADMIN:
        return <DashboardView />;

      case UserRole.TENANT:
        return <TenantDashboardView />;

      case UserRole.VENDOR:
        return <VendorDashboardView />;

      case UserRole.OWNER:
        return <OwnerDashboardView />;

      case UserRole.INVESTOR:
        return <InvestorDashboardView />;

      default:
        // Unrecognized role - error handling without console
        return (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <AlertCircle size={48} className="text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Role Configuration Error</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-2">
              Your user account has an invalid role configuration ({userRole}).
              Please try logging out and back in.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg font-bold"
            >
              Return to Login
            </button>
          </div>
        );
    }
  };

  return (
    <div className="w-full h-full">
      {renderContent()}
    </div>
  );
};


